import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Upload, X } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { PrimaryButton, OutlineButton } from './Buttons';
import CustomInput from './CustomInput';
import axiosInstance from '../services/api/axios';
import { toast } from 'react-toastify';
import { FormModal } from './form';
import { getPresignedUploadUrls, uploadFilesToS3, getPresignedDownloadUrl } from '../utils/fileUtils';

// Strip HTML tags to get plain text for validation/trim
const stripHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim();
};

const RequestDesignModal = ({
  isOpen,
  onClose,
  onSuccess,
  designer,
  portfolioItems,
  featuredIterations,
  propertyId,
  propertyConfigId,
  userId,
  propertyInfo,
}) => {
  const [requirements, setRequirements] = useState('');
  const [designFeesOffered, setDesignFeesOffered] = useState('');
  // Material budget (real rupees) + overspend buffer % the ID may exceed it by.
  // The ID never sees these rupee values — backend converts them to credits.
  const [materialBudget, setMaterialBudget] = useState('');
  const [budgetBufferPercent, setBudgetBufferPercent] = useState('');
  // Reference images are uploaded straight to S3 (presigned) — we keep only
  // { s3_key, content_type } for the payload plus a presigned previewUrl for
  // display. No base64 is ever held or sent.
  const [mediaItems, setMediaItems] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const mediaInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setRequirements('');
      setDesignFeesOffered('');
      setMaterialBudget('');
      setBudgetBufferPercent('');
      setMediaItems([]);
    }
  }, [isOpen]);

  const handleMediaChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const accepted = files.filter((f) => f.type.startsWith('image/'));
    if (accepted.length < files.length) {
      toast.warning('Only image files are accepted.');
    }
    if (accepted.length === 0) {
      e.target.value = '';
      return;
    }
    setUploadingMedia(true);
    try {
      // "requirements" folder must exist in USER/CUSTOMER_ALLOWED_FOLDER_TYPES
      // (services/shared/.../schemas/media.py). BE namespaces it to
      // users/{id}/requirements/... or customers/{id}/requirements/...
      const presigned = await getPresignedUploadUrls(accepted, 'requirements', axiosInstance);
      const uploadData = presigned.map((p, i) => ({
        file: accepted[i],
        upload_url: p.upload_url,
        content_type: p.content_type || accepted[i].type,
      }));
      await uploadFilesToS3(uploadData);
      const items = [];
      for (let i = 0; i < presigned.length; i++) {
        const s3Key = presigned[i].s3_key;
        const res = await getPresignedDownloadUrl(s3Key, axiosInstance);
        items.push({
          s3_key: s3Key,
          content_type: presigned[i].content_type || accepted[i].type,
          previewUrl: res.download_url || res.downloadUrl || '',
        });
      }
      setMediaItems((prev) => [...prev, ...items]);
    } catch (err) {
      console.error('Error uploading reference images:', err);
      toast.error(err?.response?.data?.message || 'Failed to upload images.');
    } finally {
      setUploadingMedia(false);
      e.target.value = '';
    }
  };

  const removeMedia = (index) => {
    setMediaItems((prev) => prev.filter((_, i) => i !== index));
  };

  const hasContent = stripHtml(requirements).length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasContent) {
      toast.error('Please enter your requirements');
      return;
    }
    if (!designer?.interiorDesignerId) {
      toast.error('Designer information is missing');
      return;
    }
    if (!userId) {
      toast.error('User information is missing');
      return;
    }
    if (!propertyId) {
      toast.error('Property information is missing');
      return;
    }
    const parsedDesignFee =
      designFeesOffered === '' ? 0 : Number.parseFloat(designFeesOffered);
    if (Number.isNaN(parsedDesignFee) || parsedDesignFee < 0) {
      toast.error('Please enter a valid design fee (Rs/sq.ft)');
      return;
    }
    // Material budget is optional; when provided it must be a non-negative number.
    const parsedMaterialBudget =
      materialBudget === '' ? null : Number.parseFloat(materialBudget);
    if (parsedMaterialBudget !== null && (Number.isNaN(parsedMaterialBudget) || parsedMaterialBudget < 0)) {
      toast.error('Please enter a valid material budget (₹)');
      return;
    }
    const parsedBufferPercent =
      budgetBufferPercent === '' ? 0 : Number.parseFloat(budgetBufferPercent);
    if (Number.isNaN(parsedBufferPercent) || parsedBufferPercent < 0) {
      toast.error('Please enter a valid budget buffer %');
      return;
    }

    try {
      setSubmitting(true);
      const portfolioItemIds = (portfolioItems || [])
        .map((item) => item.interiorDesignerPortfolioItemId)
        .filter(Boolean);

      const featuredIterationIds = (featuredIterations || [])
        .map((item) => item.featuredDesignIterationId)
        .filter(Boolean);

      // Reference images as S3 keys (never base64).
      const mediaPayload = mediaItems.map((m) => ({
        s3_key: m.s3_key,
        content_type: m.content_type,
      }));

      const payload = [
        {
          interiorDesignerId: Number(designer.interiorDesignerId),
          userId: Number(userId),
          propertyId: Number(propertyId),
          interiorDesignerPortfolioItemIds: portfolioItemIds.map((id) => Number(id)),
          featuredDesignIterationIds: featuredIterationIds.map((id) => Number(id)),
          requirements: requirements.trim(),
          designFeesOffered: parsedDesignFee,
          materialBudget: parsedMaterialBudget,
          budgetBufferPercent: parsedBufferPercent,
          media: mediaPayload,
        },
      ];

      await axiosInstance.post('/interior-designers/leads', payload);
      toast.success('Design request sent successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error sending design request:', error);
      toast.error(error.response?.data?.message || 'Failed to send design request');
    } finally {
      setSubmitting(false);
    }
  };

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        ['blockquote', 'link'],
        ['clean'],
      ],
    }),
    []
  );

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'blockquote', 'link',
  ];

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .request-design-quill .ql-toolbar { border: none; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
        .request-design-quill .ql-container { border: none; flex: 1; display: flex; flex-direction: column; min-height: 0; }
        .request-design-quill .ql-editor { flex: 1; min-height: 200px; font-size: 14px; }
        .request-design-quill.quill { display: flex; flex-direction: column; min-height: 0; }
      `}</style>
      <FormModal
        open={isOpen}
        onClose={onClose}
        title="Request Design"
        size="2xl"
      >
        <p className="text-sm text-gray-600 mb-4">Send a design request to the interior designer</p>

        {/* Side-by-side: details (left) + requirements (right); stacks on small screens */}
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <div className="flex-1 flex flex-col md:flex-row min-h-0 overflow-hidden">
            {/* Left: Request details */}
            <div className="w-full md:w-72 shrink-0 md:border-r border-b md:border-b-0 border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-800">Request details</h3>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0">
                {designer && (
                  <div className="flex items-start gap-2.5">
                   {/* <User className="text-gray-400 shrink-0 mt-0.5" size={16} /> */}
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">Designer</p>
                      <p className="text-sm font-medium text-gray-900">{designer.name || 'N/A'}</p>
                     
                    </div>
                  </div>
                )}
                {propertyInfo?.propertyNo && (
                  <div className="flex items-start gap-2.5">
                    {/* <Building2 className="text-gray-400 shrink-0 mt-0.5" size={16} /> */}
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">Property</p>
                      <p className="text-sm font-medium text-gray-900">Unit {propertyInfo.propertyNo}</p>
                      {propertyInfo.configName && (
                        <p className="text-xs text-gray-600 mt-0.5">{propertyInfo.configName}</p>
                      )}
                    </div>
                  </div>
                )}
                {propertyInfo?.location && (
                  <div className="flex items-start gap-2.5">
                    {/* <MapPin className="text-gray-400 shrink-0 mt-0.5" size={16} /> */}
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">Location</p>
                      <p className="text-sm font-medium text-gray-900">{propertyInfo.location}</p>
                    </div>
                  </div>
                )}
                {(portfolioItems?.length > 0 || featuredIterations?.length > 0) && (
                  <div className="flex items-start gap-2.5">
                    {/* <Palette className="text-gray-400 shrink-0 mt-0.5" size={16} /> */}
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">Shortlisted</p>
                      <p className="text-sm font-medium text-gray-900">
                        {(portfolioItems?.length || 0) + (featuredIterations?.length || 0)} design{((portfolioItems?.length || 0) + (featuredIterations?.length || 0)) !== 1 ? 's' : ''} selected
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Requirements editor + Media */}
            <div className="flex-1 flex flex-col min-w-0 min-h-0 p-6 overflow-y-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1 shrink-0">
                Requirements <span className="text-red-500">*</span>
              </label>
              <div className="min-h-[200px] flex flex-col rounded-md border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-flamingo-500 focus-within:border-flamingo-500 shrink-0">
                <ReactQuill
                  theme="snow"
                  value={requirements}
                  onChange={setRequirements}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Describe your design requirements, preferences, budget, timeline, and any specific needs. You can use formatting (bold, lists, etc.)."
                  className="request-design-quill flex-1 flex flex-col bg-white"
                  readOnly={submitting}
                  style={{ minHeight: '180px' }}
                />
              </div>

              <div className="mt-4 shrink-0">
                <CustomInput
                  label="Design Fees Offered (Rs/sq.ft)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={designFeesOffered}
                  onChange={(e) => setDesignFeesOffered(e.target.value)}
                  placeholder="Enter offered design fee per sq.ft"
                  disabled={submitting}
                />
                <p className="mt-1 text-xs text-gray-500">This value is treated as a per sq.ft rate.</p>
              </div>

              {/* Material budget + buffer — the designer sees these only as credits */}
              <div className="mt-4 shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <CustomInput
                    label="Material Budget (₹)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={materialBudget}
                    onChange={(e) => setMaterialBudget(e.target.value)}
                    placeholder="Optional total material budget"
                    disabled={submitting}
                  />
                  <p className="mt-1 text-xs text-gray-500">Shown to the designer as credit points, not rupees.</p>
                </div>
                <div>
                  <CustomInput
                    label="Budget Buffer (%)"
                    type="number"
                    min="0"
                    step="0.1"
                    value={budgetBufferPercent}
                    onChange={(e) => setBudgetBufferPercent(e.target.value)}
                    placeholder="0"
                    disabled={submitting}
                  />
                  <p className="mt-1 text-xs text-gray-500">Allowed overspend beyond the budget.</p>
                </div>
              </div>

              {/* Media upload - consistent with DesignSubmissionModal */}
              <div className="mt-4 shrink-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Media (optional)
                </label>
                <div className={`border-2 border-dashed border-gray-300 rounded-lg p-6 transition-opacity ${submitting ? 'opacity-60 pointer-events-none' : ''}`}>
                  <input
                    ref={mediaInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleMediaChange}
                    className="hidden"
                    id="request-design-media-upload"
                    disabled={submitting}
                  />
                  <label
                    htmlFor="request-design-media-upload"
                    className={`flex flex-col items-center justify-center ${submitting || uploadingMedia ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <Upload className="text-gray-400 mb-2" size={32} />
                    <span className="text-sm text-gray-600 font-medium">
                      {uploadingMedia ? 'Uploading…' : 'Click to upload or drag and drop'}
                    </span>
                    <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB</span>
                  </label>
                </div>

                {/* Image preview grid - consistent with DesignSubmissionModal */}
                {mediaItems.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 mb-2">Selected images</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {mediaItems.map((item, index) => (
                        <div
                          key={item.s3_key || index}
                          className="relative group border border-gray-200 rounded-lg p-2 bg-gray-50/50"
                        >
                          <img
                            src={item.previewUrl}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-32 object-cover rounded border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeMedia(index)}
                            disabled={submitting}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                            aria-label="Remove"
                          >
                            <X size={14} />
                          </button>
                          <p className="text-xs text-gray-500 mt-1 truncate">Image {index + 1}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 flex justify-end gap-3 shrink-0">
            <OutlineButton
              onClick={onClose}
              disabled={submitting}
              className="px-6 py-2"
            >
              Cancel
            </OutlineButton>
            <PrimaryButton
              onClick={handleSubmit}
              disabled={submitting || uploadingMedia || !hasContent}
              className="px-6 py-2 flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                'Send Request'
              )}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>
    </>
  );
};

export default RequestDesignModal;
